import { GridRowId } from '@mui/x-data-grid/models/gridRows';
import { GridApiCommunity } from '@mui/x-data-grid/models/api/gridApiCommunity';
import { orderDetailsState as orderDetailsStateCreate } from '../../../create/states/orderDetails/orderDetails.state.ts';
import { orderDetailsState as orderDetailsStateEdit } from '../../../edit/states/orderDetails/orderDetails.state.ts';
import { offerDetailsState } from '../../../../offer/states/offerDetails/offerDetails.state.ts';
import { shouldHorizontalScroll } from '../../../../../shared/components/table/functions/sharedFunc.ts';
import { storageHelper } from '../../../../../shared/helpers/storage';

export class EnterKeyNavigation {
  private fields: Array<string> = [];
  private readonly type: Type = 'create';
  constructor(args: { sessionFields?: Array<string>; type: Type }) {
    const { sessionFields, type } = args;
    this.fields = sessionFields || [];
    this.type = type;
  }
  setKeyCombination(fields: Array<string>): void {
    this.fields = fields;
    storageHelper.local.setItem('order_offer.keyCombination', fields);
  }
  getKeyCombination(): Array<string> {
    return this.fields;
  }
  private getState() {
    if (this.type === 'offer') return offerDetailsState;
    return this.type === 'create' ? orderDetailsStateCreate : orderDetailsStateEdit;
  }
  /**
   * Moves focus to the next cell of the Enter sequence.
   *
   * @returns `true` when no focus target existed yet because a brand-new row
   * had to be requested instead: the row is created asynchronously, so the
   * cell to focus only exists later (see the grids' `onRowsCountChange`).
   * Until then the cell the user just left still holds DOM focus, and any
   * keystroke arriving in between would be turned by MUI into "start editing
   * that cell" — wiping the value just entered there (WA-3671). The caller
   * uses this flag to park focus for the duration of the handoff.
   */
  handleEnterKey(field: string, rowId: GridRowId, api: GridApiCommunity): boolean {
    const {
      getAllColumns,
      getSortedRows,
      scroll,
      getRootDimensions,
      getScrollPosition,
      getColumn,
      getColumnPosition,
      setCellFocus,
    } = api;
    const isCurrentFieldInFields = !!this.fields.find((el) => el === field);
    const currentColumn = getColumn(field);
    const currentTemp = getAllColumns().reduce((acc: string[], item) => {
      if (
        (!isCurrentFieldInFields || this.fields.length === 0 || this.fields.includes(item.field)) &&
        (!currentColumn.editable || item.editable) &&
        item.field !== '__reorder__'
      )
        acc.push(item.field);
      return acc;
    }, []);

    const fieldIndex = currentTemp.findIndex((el) => el === field);
    const containerWidth = getRootDimensions()?.viewportInnerSize?.width || 0;
    const { left: scrollLeft } = getScrollPosition();
    if (fieldIndex === currentTemp.length - 1) {
      const filteredRows = getSortedRows().filter(
        (el) => !el.virtualPositionId && (this.type === 'offer' || el.isChangeable),
      );
      const rowIndex = filteredRows.findIndex((el) => el.id === rowId);
      const nextRow = filteredRows[rowIndex + 1];
      const { width: columnWidth = 0 } = getColumn(currentTemp[0]);
      const columnLeft = getColumnPosition(currentTemp[0]);
      const needToScroll = shouldHorizontalScroll({
        scrollLeft,
        columnLeft,
        columnWidth,
        containerWidth,
      });
      if (nextRow) {
        this.getState().pub.selectPosition(nextRow as any);
        setCellFocus(nextRow.id as string, currentTemp[0]);
        if (needToScroll) {
          scroll({ left: columnLeft });
        }
      } else {
        this.getState().pub.addPosition({
          type: 'addPosition',
          // Enter navigation runs after MUI has already committed and left
          // edit mode synchronously, so the 50 ms grace period the toolbar
          // "+" button needs is pure latency here — and latency is exactly
          // what the WA-3671 focus race feeds on.
          immediate: true,
        });
        if (needToScroll) {
          scroll({ left: columnLeft });
        }
        return true;
      }
    } else {
      const nextField = currentTemp[fieldIndex + 1];
      const { width: columnWidth = 0 } = getColumn(nextField);
      const columnLeft = getColumnPosition(nextField);
      const needToScroll = shouldHorizontalScroll({
        scrollLeft,
        columnLeft,
        columnWidth,
        containerWidth,
      });
      if (needToScroll) {
        scroll({ left: columnLeft });
      }
      setCellFocus(rowId, nextField);
    }
    return false;
  }
}

type Type = 'create' | 'edit' | 'offer';
